import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { getClientConfig } from '../../config/clientConfig';
import HELPERS from '../../utils/helper';
import { Toast, toastMsgConfig } from '../toast';
import { INDIA_STATES, getDistricts } from '../../data/indiaLocations';
import mdtseBanner from './mdtse-banner.jpg';
import './template16.css';

// The register API expects the password base64 encoded (e.g. "123456" -> "MTIzNDU2").
// Encode through TextEncoder so non-ASCII characters survive the round trip.
const encodePassword = (value) => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach(byte => { binary += String.fromCharCode(byte); });
  return window.btoa(binary);
};

// Length of the OTP the login API sends.
const OTP_LENGTH = 4;

// The test API returns dates as YYYY-MM-DD and times as 24 hour HH:mm.
const formatExamDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
};

const formatExamTime = (value) => {
  if (!value) return '';
  const [hours, minutes] = value.split(':');
  const hour = Number(hours);
  if (Number.isNaN(hour)) return value;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minutes} ${suffix}`;
};

// "21/08/2026 ( 7:30 AM to 7:31 AM )"
const formatExamSlot = (test) => {
  if (!test) return '';
  const date = formatExamDate(test.start_date);
  const from = formatExamTime(test.start_time);
  const to = formatExamTime(test.end_time);
  const timeWindow = [from, to].filter(Boolean).join(' to ');
  if (date && timeWindow) return `${date} ( ${timeWindow} )`;
  return date || timeWindow;
};

/* --------------------------------------------------------------------------
   Static exam content - sourced from the MDTSE 2026 prospectus.
   Kept at module level so the copy can be edited without touching the JSX.
   -------------------------------------------------------------------------- */

const EXAM_PHASES = [
  { name: 'Phase 1', date: '13 September 2026', day: 'Sunday', mode: 'Online', time: '9:00 AM to 7:00 PM' },
  { name: 'Phase 2', date: '20 September 2026', day: 'Sunday', mode: 'Online', time: '9:00 AM to 7:00 PM' },
  { name: 'Final Round', date: '11 October 2026', day: 'Sunday', mode: 'Offline', time: '10:30 AM to 11:30 AM' }
];

const EXAM_INFO = [
  { icon: 'fa-user-graduate', label: 'Eligibility', value: 'Class VI to XII', sub: 'Studying students' },
  { icon: 'fa-language', label: 'Medium', value: 'English', sub: 'Class X - both medium' },
  { icon: 'fa-location-dot', label: 'Offline Exam Center', value: 'MD Campus, Chirawa', sub: 'Jhunjhunu Road' },
  { icon: 'fa-indian-rupee-sign', label: 'Exam Fee', value: 'Free of Cost', sub: 'Online & offline both' },
  { icon: 'fa-calendar-check', label: 'Result Declaration', value: 'Online: 27 Sep 2026', sub: 'Offline: 18 Oct 2026' },
  { icon: 'fa-school', label: 'Board Exam Support', value: 'Full Support', sub: 'By MDCL teachers' }
];

const REWARDS = [
  { icon: 'fa-percent', value: 'Up to 100%', title: 'Scholarship', desc: 'On NEET, JEE & Foundation courses for qualifying students.' },
  { icon: 'fa-sack-dollar', value: 'Up to 125 Lakh*', title: 'Scholarships & Cash Awards', desc: 'Total reward pool distributed among top performers.' },
  { icon: 'fa-trophy', value: 'Topper Rank', title: 'Rank & Recognition', desc: 'Felicitation and recognition for every topper across classes.' }
];

const FEATURES = [
  { icon: 'fa-user-tie', title: 'Expert Faculty Guidance', desc: 'Learn directly from experienced MDCL mentors.' },
  { icon: 'fa-book-open', title: 'Comprehensive Study Material', desc: 'Structured notes and practice sets for every chapter.' },
  { icon: 'fa-users', title: 'Personalized Mentorship', desc: 'One-to-one guidance mapped to each student.' },
  { icon: 'fa-clipboard-list', title: 'Chapter-wise Test Series', desc: 'Regular assessments to track real progress.' },
  { icon: 'fa-chart-line', title: 'Proven Results Every Year', desc: 'A consistent record of selections and top ranks.' },
  { icon: 'fa-graduation-cap', title: 'Board Exam Support', desc: 'Full board preparation support by MDCL teachers.' }
];

const HOW_IT_WORKS = [
  { icon: 'fa-pen-to-square', title: 'Register for free', desc: 'Fill this form - it takes about 2 minutes. No registration fee.' },
  { icon: 'fa-key', title: 'Get your login', desc: 'Sign in with your mobile number and the password you create here.' },
  { icon: 'fa-laptop', title: 'Appear for the test', desc: '40 questions, 100 marks, 1 hour. No negative marking.' },
  { icon: 'fa-trophy', title: 'Win your scholarship', desc: 'Up to 100% scholarship plus cash awards for top performers.' }
];

const PATTERN = [
  { value: '40', sub: 'Questions', label: 'Total No. of Questions' },
  { value: '100', sub: 'Marks', label: 'Total Marks' },
  { value: '1', sub: 'Hour', label: 'Duration' },
  { value: 'NIL', sub: 'Negative', label: 'No Negative Marking' }
];

const SUBJECT_ICONS = {
  PHYSICS: 'fa-bolt',
  CHEMISTRY: 'fa-flask',
  BIOLOGY: 'fa-leaf',
  BOTANY: 'fa-seedling',
  ZOOLOGY: 'fa-paw',
  MATHEMATICS: 'fa-calculator',
  'MENTAL ABILITY': 'fa-brain'
};

const SYLLABUS = [
  {
    key: 'VI',
    tab: 'Class VI',
    title: 'For Class VI Studying Students',
    marks: '40 MCQs: Science (24) | Mathematics (8) | Mental Ability (8)',
    subjects: [
      { name: 'PHYSICS', topics: ['Measurement', 'Motion', 'Force'] },
      { name: 'CHEMISTRY', topics: ['Acids, base & salt', 'Physical & Chemical change'] },
      { name: 'BIOLOGY', topics: ['Food and its varieties', 'Nutrition in plants', 'Diversity in the living world'] },
      { name: 'MATHEMATICS', topics: ['Numbers', 'Factors and multiples', 'Fractions', 'Decimals', 'Geometry', 'Measurement'] },
      { name: 'MENTAL ABILITY', topics: ['Time and calendar', 'Blood Relation', 'Direction test', 'Coding - Decoding', 'Series', 'Analogy'] }
    ]
  },
  {
    key: 'VII',
    tab: 'Class VII',
    title: 'For Class VII Studying Students',
    marks: '40 MCQs: Science (24) | Mathematics (8) | Mental Ability (8)',
    subjects: [
      { name: 'PHYSICS', topics: ['Measurement', 'Motion', 'Force'] },
      { name: 'CHEMISTRY', topics: ['Separation of substances (winnowing, evaporation, decantation)'] },
      { name: 'BIOLOGY', topics: ['Nutrition in plants', 'Nutrition in animals', 'Respiration in organisms', 'Transportation in animals and plants'] },
      { name: 'MATHEMATICS', topics: ['Integers', 'Ratio', 'Fractions', 'Linear equation', 'Quadrilaterals', 'Perimeter and area'] },
      { name: 'MENTAL ABILITY', topics: ['Seating Arrangement', 'Blood Relation', 'Mathematical operations', 'Coding - Decoding', 'Series', 'Analogy', 'English dictionary'] }
    ]
  },
  {
    key: 'VIII',
    tab: 'Class VIII',
    title: 'For Class VIII Studying Students',
    marks: '40 MCQs: Science (24) | Mathematics (8) | Mental Ability (8)',
    subjects: [
      { name: 'PHYSICS', topics: ['Light', 'Force', 'Chemical effect of electric current'] },
      { name: 'CHEMISTRY', topics: ['Acids, base & salt', 'Physical & chemical change'] },
      { name: 'BIOLOGY', topics: ['Crop production and management', 'Microorganisms friend and foe', 'Conservation of plant and animals', 'Reproduction in animals'] },
      { name: 'MATHEMATICS', topics: ['Exponent and powers', 'Square and square root', 'Profit and loss', 'Speed, time and distance', 'Geometry', 'Mensuration'] },
      { name: 'MENTAL ABILITY', topics: ['Seating Arrangement', 'Blood Relation', 'Mathematical operations', 'Coding - Decoding', 'Series', 'Algebra', 'Direction test'] }
    ]
  },
  {
    key: 'IX',
    tab: 'Class IX',
    title: 'For Class IX Studying Students',
    marks: '40 MCQs: Science (24) | Mathematics (8) | Mental Ability (8)',
    subjects: [
      { name: 'PHYSICS', topics: ['Motion', 'Force and Laws of Motion', 'Gravitation'] },
      { name: 'CHEMISTRY', topics: ['Matter in Our Surroundings', 'Is Matter Around Us Pure?', 'Atoms and Molecules'] },
      { name: 'BIOLOGY', topics: ['The Fundamental Unit of Life', 'Tissues'] },
      { name: 'MATHEMATICS', topics: ['Number Systems', 'Polynomials', 'Coordinate Geometry', 'Lines and Angles', 'Triangles', 'Linear Equations in Two Variables'] },
      { name: 'MENTAL ABILITY', topics: ['Verbal & Non-Verbal Reasoning: Series, Analogy, Classification', 'Coding and Decoding', 'General Logical and Analytical Reasoning (Mathematical Operation, Cube & Dice, Calendar, Clock Test, Blood Relation, Direction Test)', 'Figure Matrix', 'Problems Based on Figures'] }
    ]
  },
  {
    key: 'X',
    tab: 'Class X',
    title: 'For Class X Studying Students (H.E. Both Medium)',
    marks: '40 MCQs: Physics (8) | Chemistry (8) | Biology (8) | Mental Ability (8) | Mathematics (8)',
    subjects: [
      { name: 'PHYSICS', topics: ['Light - Reflection and Refraction', 'Human Eye and Colourful World', 'Electricity'] },
      { name: 'CHEMISTRY', topics: ['Chemical Reactions & Equations', 'Acids, Bases and Salts', 'Metals and Non-Metals'] },
      { name: 'BIOLOGY', topics: ['Life Processes', 'Control and Coordination', 'How do Organisms Reproduce?'] },
      { name: 'MATHEMATICS', topics: ['Real Numbers', 'Polynomials', 'Pair of Linear Equations in Two Variables', 'Quadratic Equations', 'Arithmetic Progressions', 'Introduction to Trigonometry', 'Some Applications of Trigonometry'] },
      { name: 'MENTAL ABILITY', topics: ['Verbal & Non-verbal Reasoning: Series, Analogy, Classification', 'Coding and Decoding', 'General Logical and Analytical Reasoning (Mathematical Operation, Cube & Dice, Calendar, Clock Test, Blood Relation, Direction Test)', 'Problems Based on Figures', 'Data Interpretation'] }
    ]
  },
  {
    key: 'XI-MED',
    tab: 'Class XI - Medical',
    title: 'For Class XI Studying Students (Medical Aspirants)',
    marks: '40 MCQs: Physics (10) | Chemistry (10) | Biology (20)',
    subjects: [
      { name: 'PHYSICS', topics: ['Units & Measurements', 'Motion in a Straight Line', 'Motion in a Plane', 'Laws of Motion', 'Work, Energy and Power', 'System of Particles and Rotational Motion'] },
      { name: 'CHEMISTRY', topics: ['Some Basic Concepts of Chemistry', 'Structure of Atom', 'Classification of Elements and Periodicity', 'Redox Reactions', 'Basic Principles and Techniques'] },
      { name: 'BOTANY', topics: ['Cell: The Unit of Life', 'The Living World', 'Biological Classification', 'Morphology of Flowering Plants', 'Plant Anatomy'] },
      { name: 'ZOOLOGY', topics: ['Animal Kingdom', 'Breathing and Exchange of Gases', 'Body Fluids and Circulation', 'Structural Organization in Animals (Frog)'] }
    ]
  },
  {
    key: 'XI-ENG',
    tab: 'Class XI - Engineering',
    title: 'For Class XI Studying Students (Engineering Aspirants)',
    marks: '40 MCQs: Physics (13) | Chemistry (13) | Mathematics (14)',
    subjects: [
      { name: 'PHYSICS', topics: ['Physical World', 'Units & Measurement', 'Motion in a Straight Line', 'Motion in a Plane', 'Laws of Motion', 'Work, Energy and Power', 'System of Particles & Rotational Motion'] },
      { name: 'CHEMISTRY', topics: ['Some Basic Concepts of Chemistry', 'Structure of Atom', 'Classification of Elements & Periodicity', 'Redox Reaction', 'Basic principal and Techniques'] },
      { name: 'MATHEMATICS', topics: ['Sets', 'Trigonometric Functions', 'Complex Numbers', 'Relation and function', 'Permutations and Combinations', 'Sequences and Series'] }
    ]
  },
  {
    key: 'XII-MED',
    tab: 'Class XII - Medical',
    title: 'For Class XII Studying Students (Medical Aspirants)',
    marks: '40 MCQs: Physics (10) | Chemistry (10) | Biology (20)',
    subjects: [
      { name: 'PHYSICS', topics: ['Electric Charges and Fields', 'Electrostatic Potential and Capacitance', 'Current Electricity', 'Moving Charges & Magnetism', 'Magnetism and Matter', 'Electromagnetic Induction', 'Alternating Current'] },
      { name: 'CHEMISTRY', topics: ['Solutions', 'Electrochemistry', 'Chemical Kinetics', 'The d- & f-Block Elements', 'Coordination Compounds'] },
      { name: 'BOTANY', topics: ['Sexual Reproduction in Flowering Plants', 'Principles of Inheritance and Variation', 'Molecular Basis of Inheritance', 'Microbes in Human Welfare'] },
      { name: 'ZOOLOGY', topics: ['Human Reproduction', 'Reproductive Health', 'Human Health & Disease', 'Biotechnology: Principles and Processes', "Biotechnology and it's Applications"] }
    ]
  },
  {
    key: 'XII-ENG',
    tab: 'Class XII - Engineering',
    title: 'For Class XII Studying Students (Engineering Aspirants)',
    marks: '40 MCQs: Physics (13) | Chemistry (13) | Mathematics (14)',
    subjects: [
      { name: 'PHYSICS', topics: ['Electric Charges & Fields', 'Electrostatic Potential and Capacitance', 'Current Electricity', 'Moving Charges & Magnetism'] },
      { name: 'CHEMISTRY', topics: ['Solution', 'Electrochemistry', 'Chemical Kinetics', 'The d & f-Block elements', 'Coordination Compounds'] },
      { name: 'MATHEMATICS', topics: ['Inverse Trigonometric Functions', 'Matrices', 'Determinants', 'Continuity and Differentiability', 'Application of Derivatives', 'Integrals', 'Application of Integrals'] }
    ]
  }
];

const FAQS = [
  {
    q: 'Who can appear for MDTSE 2026?',
    a: 'Students currently studying in Class VI to Class XII are eligible. Class XII passed students preparing for Medical or Engineering entrance exams may also participate.'
  },
  {
    q: 'Is there any registration or exam fee?',
    a: 'No. Registration is completely free and the exam is free of cost in both online and offline modes.'
  },
  {
    q: 'What is the exam pattern?',
    a: '40 multiple-choice questions for 100 marks in 1 hour, with no negative marking. The subject-wise split depends on the class you are studying in.'
  },
  {
    q: 'What can I win through MDTSE?',
    a: 'Qualifying students receive up to 100% scholarship on NEET, JEE and Foundation courses. Scholarships and cash awards worth up to 125 Lakh* are distributed among top performers, along with topper rank recognition.'
  },
  {
    q: 'Where is the offline final round conducted?',
    a: 'The offline final round is held at MD Campus, Jhunjhunu Road, Chirawa on 11 October 2026 (Sunday) from 10:30 AM to 11:30 AM.'
  },
  {
    q: 'When will the results be declared?',
    a: 'Online phase results are declared on 27 September 2026 (Sunday) and offline final round results on 18 October 2026 (Sunday).'
  }
];

class EnquiryTemplate16 extends Component {
  constructor(props) {
    super(props);
    const clientConfig = getClientConfig(props.clientId);

    this.formRef = React.createRef();

    this.state = {
      clientConfig: clientConfig,
      formData: {
        name: '',
        email: '',
        phone: '',
        standardId: '',
        standardTitle: '',
        testId: '',
        testName: '',
        fatherName: '',
        schoolName: '',
        state: '',
        district: '',
        cityVillage: '',
        password: '',
        confirmPassword: ''
      },
      procturStates: [],
      showPassword: false,
      errors: {},
      loader: false,
      disabled: false,
      standards: [],
      standardsLoading: false,
      tests: [],
      testsLoading: false,
      selectedTest: null,
      activeSyllabus: SYLLABUS[0].key,
      openFaq: 0,
      submitMessage: null,
      showSuccessPopup: false,
      successDetail: null,
      // OTP verification stage - only entered when generate_otp reports
      // skip_otp_verification: false for this institute.
      otpRequired: false,
      otpValue: '',
      otpError: '',
      otpVerifying: false,
      otpResending: false,
      resendIn: 0
    };
  }

  componentWillUnmount() {
    if (this.resendTimer) clearInterval(this.resendTimer);
  }

  componentDidMount() {
    if (!this.state.clientConfig) return;
    window.scrollTo(0, 0);
    this.fetchStandards();
    this.fetchProcturStates();
    this.applyFavicon();
  }

  // index.html is shared by every client, so swap the icon at runtime rather
  // than hardcoding one institute's logo into the global template.
  applyFavicon = () => {
    const favicon = this.state.clientConfig.branding.favicon;
    if (!favicon) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/png';
    link.href = favicon;
  }

  // The district list is bundled, but register wants Proctur's own state_id, so
  // fetch their state master and match it up by name.
  fetchProcturStates = async () => {
    try {
      const url = `${this.state.clientConfig.api.baseUrl}/StdMgmtWebAPI/api/v1/country/state?country_ids=1`;
      const res = await axios.get(url, { headers: { Accept: 'application/json, text/plain, */*' }, timeout: 30000 });
      const procturStates = [];
      (res.data && res.data.result ? res.data.result : []).forEach(country => {
        (country.stateList || []).forEach(st => {
          if (st.state_name) procturStates.push({ id: st.state_id, name: st.state_name });
        });
      });
      this.setState({ procturStates });
    } catch (error) {
      console.warn('Could not load Proctur state list:', error.message);
    }
  }

  // Selecting a state invalidates whichever district was chosen under the old one.
  handleStateChange = (e) => {
    const state = e.target.value;
    this.setState(prevState => ({
      formData: { ...prevState.formData, state: state, district: '' },
      errors: { ...prevState.errors, state: '', district: '' }
    }));
  }

  /* ----------------------------- Online test APIs ------------------------- */

  // Online test APIs live on their own base URL (exdapi / SIT) and use a
  // separate short authorization token, both declared in clientConfig.
  getOnlineTestConfig = () => {
    const { clientConfig } = this.state;
    return (clientConfig && clientConfig.api && clientConfig.api.onlineTest) || null;
  }

  onlineTestHeaders = () => {
    const onlineTest = this.getOnlineTestConfig();
    return {
      Authorization: onlineTest.authorization,
      source: 'WEB',
      Accept: 'application/json, text/plain, */*'
    };
  }

  fetchStandards = async () => {
    const onlineTest = this.getOnlineTestConfig();
    if (!onlineTest) return;

    this.setState({ standardsLoading: true });
    try {
      const url = `${onlineTest.baseUrl}${onlineTest.endpoints.getStandards}`;
      const res = await axios.get(url, { headers: this.onlineTestHeaders() });
      const standards = (res.data && res.data.data) || [];
      this.setState({ standards, standardsLoading: false });
    } catch (error) {
      console.error('Failed to fetch standards:', error);
      this.setState({ standards: [], standardsLoading: false });
    }
  }

  fetchTests = async (standardId) => {
    const onlineTest = this.getOnlineTestConfig();
    if (!onlineTest || !standardId) return;

    this.setState({ testsLoading: true, tests: [] });
    try {
      const endpoint = onlineTest.endpoints.getTests.replace('{standardId}', standardId);
      const res = await axios.get(`${onlineTest.baseUrl}${endpoint}`, { headers: this.onlineTestHeaders() });
      const tests = (res.data && res.data.data) || [];
      this.setState({ tests, testsLoading: false });
    } catch (error) {
      console.error('Failed to fetch tests:', error);
      this.setState({ tests: [], testsLoading: false });
    }
  }

  /* -------------------------------- Handlers ------------------------------ */

  handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value;

    this.setState(prevState => ({
      formData: { ...prevState.formData, [name]: nextValue },
      errors: { ...prevState.errors, [name]: '' }
    }));
  }

  // Standard drives the exam category list, so changing it resets the test.
  handleStandardChange = (e) => {
    const standardId = e.target.value;
    const selected = this.state.standards.find(s => String(s.standard_id) === standardId);

    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        standardId: standardId,
        standardTitle: selected ? selected.title : '',
        testId: '',
        testName: ''
      },
      tests: [],
      selectedTest: null,
      errors: { ...prevState.errors, standardId: '', testId: '' }
    }), () => {
      if (standardId) this.fetchTests(standardId);
    });
  }

  handleTestChange = (e) => {
    const testId = e.target.value;
    const selected = this.state.tests.find(t => String(t.test_id) === testId);

    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        testId: testId,
        testName: selected ? selected.test_name : ''
      },
      selectedTest: selected || null,
      errors: { ...prevState.errors, testId: '' }
    }));
  }

  scrollToForm = () => {
    if (this.formRef.current) {
      this.formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // A slot is identified by its date and time window, not by its name - the
  // names repeat across the list anyway.
  formatTestLabel = (test) => formatExamSlot(test) || test.test_name

  /* ------------------------------- Validation ----------------------------- */

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Student name is required';
    } else if (!HELPERS.validateName(formData.name)) {
      errors.name = 'Please enter a valid name';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email ID is required';
    } else if (!HELPERS.validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Mobile number is required';
    } else if (!/^[6-9][0-9]{9}$/.test(formData.phone)) {
      errors.phone = 'Enter a valid 10-digit mobile number';
    }

    if (!formData.standardId) {
      errors.standardId = 'Please select your standard';
    }

    // Only demand a slot when the chosen standard actually has one on offer;
    // otherwise a standard with no configured slots would lock the student out.
    if (!formData.testId && this.state.tests.length > 0) {
      errors.testId = 'Please select an exam slot';
    }

    if (!formData.fatherName.trim()) {
      errors.fatherName = "Father's name is required";
    }

    if (!formData.schoolName.trim()) {
      errors.schoolName = 'Current school name is required';
    }

    if (!formData.state) {
      errors.state = 'Please select your state';
    }

    if (!formData.district) {
      errors.district = 'Please select your district';
    }

    if (!formData.cityVillage.trim()) {
      errors.cityVillage = 'City or village is required';
    }

    if (!formData.password) {
      errors.password = 'Please create a password';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  /* -------------------------------- Submit -------------------------------- */

  handleSubmit = async (e) => {
    e.preventDefault();

    if (!this.validateForm()) {
      this.setState({ submitMessage: { type: 'e', text: 'Please correct the highlighted fields.' } });
      return;
    }

    if (!HELPERS.checkRateLimit('mdtse_register', 5, 60000)) {
      this.setState({ submitMessage: { type: 'e', text: 'Too many attempts. Please try again in a minute.' } });
      return;
    }

    this.setState({ loader: true, disabled: true, submitMessage: null });

    try {
      // Step 1 - ask for an OTP. The response tells us whether this institute
      // actually requires verification.
      const otpResult = await this.sendOtp();

      if (otpResult.skipVerification) {
        // Institute has verification switched off - register straight away.
        await this.registerStudent();
      } else {
        // Show the verification screen; register runs after validate_otp passes.
        this.setState({
          loader: false,
          disabled: false,
          otpRequired: true,
          otpValue: '',
          otpError: ''
        });
        this.startResendCountdown();
      }
    } catch (error) {
      const apiMessage = error.response && error.response.data && error.response.data.message;
      const message = apiMessage || error.message || 'Registration failed. Please check your connection and try again.';
      Toast(message, toastMsgConfig('e'));
      this.setState({ loader: false, disabled: false, submitMessage: { type: 'e', text: message } });
    }
  }

  /* ------------------------------ OTP handling ---------------------------- */

  apiHeaders = () => {
    const { authorization } = this.state.clientConfig.api;
    // Both endpoints are institute scoped. Missing token -> generic 500,
    // wrong token -> 404 "Institute not found", so fail early and clearly.
    if (!authorization || authorization.startsWith('REPLACE_WITH_')) {
      throw new Error('Registration is not configured yet. Please contact the institute.');
    }
    return {
      'Authorization': authorization,
      'Content-Type': 'application/json',
      'source': 'WEB',
      'accept': 'application/json, text/plain, */*'
    };
  }

  // Sends the OTP and reports whether this institute skips verification.
  sendOtp = async () => {
    const { clientConfig, formData } = this.state;
    const { baseUrl, endpoints } = clientConfig.api;

    const res = await axios.post(`${baseUrl}${endpoints.generateOtp}`, {
      user_name: formData.phone,
      source: 'register',
      app_hash_code: '',
      role: 99,
      country_id: 1,
      email_id: formData.email.trim()
    }, { headers: this.apiHeaders(), timeout: 30000 });

    const result = (res.data && res.data.result) || {};
    return { skipVerification: result.skip_otp_verification === true };
  }

  startResendCountdown = (seconds = 30) => {
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.setState({ resendIn: seconds });
    this.resendTimer = setInterval(() => {
      this.setState(prev => {
        if (prev.resendIn <= 1) {
          clearInterval(this.resendTimer);
          return { resendIn: 0 };
        }
        return { resendIn: prev.resendIn - 1 };
      });
    }, 1000);
  }

  handleOtpChange = (e) => {
    this.setState({ otpValue: e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH), otpError: '' });
  }

  handleResendOtp = async () => {
    if (this.state.resendIn > 0 || this.state.otpResending) return;
    this.setState({ otpResending: true, otpError: '' });
    try {
      await this.sendOtp();
      Toast('A new OTP has been sent to your mobile number.', toastMsgConfig('s'));
      this.setState({ otpResending: false, otpValue: '' });
      this.startResendCountdown();
    } catch (error) {
      const apiMessage = error.response && error.response.data && error.response.data.message;
      this.setState({
        otpResending: false,
        otpError: apiMessage || 'Could not resend the OTP. Please try again.'
      });
    }
  }

  handleVerifyOtp = async (e) => {
    e.preventDefault();
    const { otpValue, formData, clientConfig } = this.state;

    if (otpValue.length < OTP_LENGTH) {
      this.setState({ otpError: `Please enter the ${OTP_LENGTH}-digit OTP.` });
      return;
    }

    this.setState({ otpVerifying: true, otpError: '' });

    try {
      const { baseUrl } = clientConfig.api;
      const res = await axios.post(`${baseUrl}/StdMgmtWebAPI/api/v2/user/login/validate_otp`, {
        user_name: formData.phone,
        otp_code: otpValue,
        source: 'register',
        role: 99,
        country_id: 1
      }, { headers: this.apiHeaders(), timeout: 30000 });

      const ok = res.data && (res.data.validate === true || res.data.statusCode === 200);
      if (!ok) {
        this.setState({ otpVerifying: false, otpError: 'OTP is incorrect, please try again.' });
        return;
      }

      // Verified - now create the account.
      this.setState({ otpVerifying: false, loader: true, disabled: true });
      await this.registerStudent();
    } catch (error) {
      const apiMessage = error.response && error.response.data && error.response.data.message;
      // The API returns "OTP Expire" once the code is past its validity window.
      const expired = apiMessage && /expire/i.test(apiMessage);
      this.setState({
        otpVerifying: false,
        loader: false,
        disabled: false,
        otpError: expired
          ? 'This OTP has expired. Please resend and try again.'
          : 'OTP is incorrect, please try again.'
      });
    }
  }

  handleChangeNumber = () => {
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.setState({ otpRequired: false, otpValue: '', otpError: '', resendIn: 0 });
  }

  /* ------------------------------- Registration --------------------------- */

  registerStudent = async () => {
    try {
      const { clientConfig, formData, selectedTest } = this.state;
      const { baseUrl, endpoints } = clientConfig.api;
      const headers = this.apiHeaders();

      const name = HELPERS.sanitizeInput(formData.name);
      const email = formData.email.trim();

      //
      // user_perment_address is the only free-text field register actually stores.
      // city_name needs a matching Proctur city_id (their catalogue has no
      // Jhunjhunu) and there is no school field at all - school_name, school,
      // current_school and institute are all absent from the user model, while
      // address and source are accepted then ignored. So district, city/village
      // and the school name are packed in here, labelled, to avoid losing them.
      const procturState = this.state.procturStates.find(
        st => st.name.toLowerCase() === formData.state.toLowerCase()
      );

      const res = await axios.post(`${baseUrl}${endpoints.registerUser}`, {
        name: name,
        email_id: email,
        password: encodePassword(formData.password),
        mobile_no: formData.phone,
        country_id: 1,
        device_id: null,
        login_source: 'WEB',
        parent_name: HELPERS.sanitizeInput(formData.fatherName),
        state_id: procturState ? procturState.id : 0,
        state_name: formData.state,
        // Exam slot. These no longer crash the endpoint (they did until
        // 24 Aug 2026), but the values are only retained when the test exists
        // in the ERP's own catalogue for this institute - anything else comes
        // back as "". Sent regardless so assignment works the moment the
        // slots are configured.
        standard_id: formData.standardId ? Number(formData.standardId) : 0,
        standard: formData.standardTitle,
        test_id: formData.testId ? Number(formData.testId) : 0,
        test_name: formData.testName,
        user_perment_address: [
          [formData.cityVillage, formData.district, formData.state].filter(Boolean).join(', '),
          formData.schoolName ? `School: ${HELPERS.sanitizeInput(formData.schoolName)}` : ''
        ].filter(Boolean).join(' | ')
      }, { headers, timeout: 30000 });

      const httpOk = res.status === 200 || res.status === 201;
      const bodyOk = !res.data || res.data.statusCode === undefined || res.data.statusCode === 200;

      if (httpOk && bodyOk) {
        Toast('Registration successful! Our team will contact you shortly.', toastMsgConfig('s'));
        this.setState({
          loader: false,
          disabled: false,
          errors: {},
          showSuccessPopup: true,
          successDetail: {
            name: formData.name,
            standard: formData.standardTitle,
            category: formData.testName,
            slot: formatExamSlot(selectedTest)
          },
          formData: {
            name: '', email: '', phone: '', standardId: '', standardTitle: '',
            testId: '', testName: '', fatherName: '', schoolName: '',
            state: '', district: '', cityVillage: '', password: '', confirmPassword: ''
          },
          tests: [],
          selectedTest: null,
          showPassword: false,
          submitMessage: null,
          otpRequired: false,
          otpValue: '',
          otpError: '',
          resendIn: 0
        });
        if (this.resendTimer) clearInterval(this.resendTimer);
      } else {
        const message = (res.data && res.data.message) || 'Something went wrong. Please try again.';
        this.reportRegisterFailure(message);
      }
    } catch (error) {
      // Surface the API message where there is one - it carries the useful
      // cases such as a mobile number that is already registered.
      const apiMessage = error.response && error.response.data && error.response.data.message;
      this.reportRegisterFailure(apiMessage || error.message || 'Registration failed. Please check your connection and try again.');
    }
  }

  reportRegisterFailure = (message) => {
    Toast(message, toastMsgConfig('e'));
    this.setState(prev => ({
      loader: false,
      disabled: false,
      // While the OTP screen is up the form's message strip is not visible.
      otpError: prev.otpRequired ? message : prev.otpError,
      submitMessage: prev.otpRequired ? prev.submitMessage : { type: 'e', text: message }
    }));
  }

  /* --------------------------------- Render ------------------------------- */

  renderOtpModal() {
    const { formData, otpValue, otpError, otpVerifying, otpResending, resendIn, loader } = this.state;
    const busy = otpVerifying || loader;

    return (
      <div className="t16-overlay">
        <div className="t16-otp-modal" role="dialog" aria-modal="true" aria-labelledby="t16-otp-heading">
          <button
            type="button"
            className="t16-otp-close"
            onClick={this.handleChangeNumber}
            aria-label="Close and return to the form"
            disabled={busy}
          >
            <i className="fa fa-xmark"></i>
          </button>

          <div className="t16-form-head">
            <h2 id="t16-otp-heading">Verify Your Mobile Number</h2>
            <p>One last step to complete your registration</p>
          </div>

          <div className="t16-form-body">
          <div className="t16-otp-intro">
            <div className="t16-otp-icon"><i className="fa fa-mobile-screen-button"></i></div>
            <p>
              We have sent a {OTP_LENGTH}-digit OTP to<br />
              <strong>+91 {formData.phone}</strong>
            </p>
            <button type="button" className="t16-otp-change" onClick={this.handleChangeNumber}>
              <i className="fa fa-pen"></i> Change number
            </button>
          </div>

          <form onSubmit={this.handleVerifyOtp} noValidate>
            <div className="t16-field">
              <label htmlFor="t16-otp">Enter OTP <span className="t16-req">*</span></label>
              <input
                id="t16-otp"
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                name="otpValue"
                value={otpValue}
                onChange={this.handleOtpChange}
                placeholder={'-'.repeat(OTP_LENGTH)}
                maxLength={OTP_LENGTH}
                autoFocus
                className={`t16-input t16-otp-input ${otpError ? 't16-input-err' : ''}`}
              />
              {otpError && <span className="t16-err">{otpError}</span>}
            </div>

            <button type="submit" className="t16-submit" disabled={busy}>
              {busy
                ? <><i className="fa fa-spinner fa-spin"></i> {otpVerifying ? 'Verifying...' : 'Registering...'}</>
                : <>Verify &amp; Register <i className="fa fa-arrow-right"></i></>}
            </button>

            <div className="t16-otp-resend">
              {resendIn > 0 ? (
                <span>Resend OTP in {resendIn}s</span>
              ) : (
                <button type="button" onClick={this.handleResendOtp} disabled={otpResending}>
                  {otpResending
                    ? <><i className="fa fa-spinner fa-spin"></i> Sending...</>
                    : <><i className="fa fa-rotate-right"></i> Resend OTP</>}
                </button>
              )}
            </div>
          </form>
          </div>
        </div>
      </div>
    );
  }

  renderForm() {
    const { formData, errors, standards, standardsLoading, tests, testsLoading, loader, disabled, submitMessage, showPassword, selectedTest, clientConfig } = this.state;

    return (
      <div className="t16-form-card" ref={this.formRef} id="mdtse-register">
        <div className="t16-form-head">
          <h2>Register for MDTSE 2026</h2>
          <p>Scholarship test for Class VI to XII students</p>
          <span className="t16-form-free">Registration Free</span>
        </div>

        <div className="t16-form-body">
          {submitMessage && (
            <div className={`t16-msg t16-msg-${submitMessage.type}`}>{submitMessage.text}</div>
          )}

          <form onSubmit={this.handleSubmit} noValidate>
            <div className="t16-field">
              <label htmlFor="t16-name">Student Name <span className="t16-req">*</span></label>
              <input
                id="t16-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={this.handleChange}
                placeholder="Enter full name"
                autoComplete="name"
                className={`t16-input ${errors.name ? 't16-input-err' : ''}`}
              />
              {errors.name && <span className="t16-err">{errors.name}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-father">Father's Name <span className="t16-req">*</span></label>
              <input
                id="t16-father"
                type="text"
                name="fatherName"
                value={formData.fatherName}
                onChange={this.handleChange}
                placeholder="Enter father's name"
                className={`t16-input ${errors.fatherName ? 't16-input-err' : ''}`}
              />
              {errors.fatherName && <span className="t16-err">{errors.fatherName}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-email">Email ID <span className="t16-req">*</span></label>
              <input
                id="t16-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={this.handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                className={`t16-input ${errors.email ? 't16-input-err' : ''}`}
              />
              {errors.email && <span className="t16-err">{errors.email}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-phone">Mobile Number <span className="t16-req">*</span></label>
              <div className="t16-phone-group">
                <span className="t16-phone-prefix">+91</span>
                <input
                  id="t16-phone"
                  type="tel"
                  inputMode="numeric"
                  name="phone"
                  value={formData.phone}
                  onChange={this.handleChange}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                  autoComplete="tel"
                  className={`t16-input ${errors.phone ? 't16-input-err' : ''}`}
                />
              </div>
              {errors.phone && <span className="t16-err">{errors.phone}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-school">Current School Name <span className="t16-req">*</span></label>
              <input
                id="t16-school"
                type="text"
                name="schoolName"
                value={formData.schoolName}
                onChange={this.handleChange}
                placeholder="Name of your current school"
                className={`t16-input ${errors.schoolName ? 't16-input-err' : ''}`}
              />
              {errors.schoolName && <span className="t16-err">{errors.schoolName}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-state">State <span className="t16-req">*</span></label>
              <select
                id="t16-state"
                name="state"
                value={formData.state}
                onChange={this.handleStateChange}
                className={`t16-input t16-select ${errors.state ? 't16-input-err' : ''}`}
              >
                <option value="">Select your state</option>
                {INDIA_STATES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
              {errors.state && <span className="t16-err">{errors.state}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-district">District <span className="t16-req">*</span></label>
              <select
                id="t16-district"
                name="district"
                value={formData.district}
                onChange={this.handleChange}
                disabled={!formData.state}
                className={`t16-input t16-select ${errors.district ? 't16-input-err' : ''}`}
              >
                <option value="">
                  {formData.state ? 'Select your district' : 'Select a state first'}
                </option>
                {getDistricts(formData.state).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              {errors.district && <span className="t16-err">{errors.district}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-city">City / Village <span className="t16-req">*</span></label>
              <input
                id="t16-city"
                type="text"
                name="cityVillage"
                value={formData.cityVillage}
                onChange={this.handleChange}
                placeholder="Your city or village"
                className={`t16-input ${errors.cityVillage ? 't16-input-err' : ''}`}
              />
              {errors.cityVillage && <span className="t16-err">{errors.cityVillage}</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-standard">Standard <span className="t16-req">*</span></label>
              <select
                id="t16-standard"
                name="standardId"
                value={formData.standardId}
                onChange={this.handleStandardChange}
                disabled={standardsLoading}
                className={`t16-input t16-select ${errors.standardId ? 't16-input-err' : ''}`}
              >
                <option value="">
                  {standardsLoading ? 'Loading standards...' : 'Preparing for'}
                </option>
                {standards.map(std => (
                  <option key={std.standard_id} value={std.standard_id}>{std.title}</option>
                ))}
              </select>
              {errors.standardId && <span className="t16-err">{errors.standardId}</span>}
              {!standardsLoading && standards.length === 0 && (
                <span className="t16-hint">Standards are unavailable right now. Please refresh the page.</span>
              )}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-test">Exam Slot <span className="t16-req">*</span></label>
              <select
                id="t16-test"
                name="testId"
                value={formData.testId}
                onChange={this.handleTestChange}
                disabled={!formData.standardId || testsLoading}
                className={`t16-input t16-select ${errors.testId ? 't16-input-err' : ''}`}
              >
                <option value="">
                  {!formData.standardId
                    ? 'Preparing for'
                    : testsLoading
                      ? 'Loading exam slots...'
                      : 'Select exam slot'}
                </option>
                {tests.map(test => (
                  <option key={test.test_id} value={test.test_id}>{this.formatTestLabel(test)}</option>
                ))}
              </select>
              {errors.testId && <span className="t16-err">{errors.testId}</span>}
              {formData.standardId && !testsLoading && tests.length === 0 && (
                <span className="t16-hint">No exam slot is open for this standard yet.</span>
              )}

              {selectedTest && (
                <div className="t16-slot-detail">
                  <div className="t16-slot-row">
                    <i className="fa fa-calendar-days"></i>
                    <span>{formatExamDate(selectedTest.start_date)}</span>
                  </div>
                  <div className="t16-slot-row">
                    <i className="fa fa-clock"></i>
                    <span>{[formatExamTime(selectedTest.start_time), formatExamTime(selectedTest.end_time)].filter(Boolean).join(' to ')}</span>
                  </div>
                  <div className="t16-slot-meta">
                    {selectedTest.duration && <span>{selectedTest.duration} min</span>}
                    {selectedTest.total_question > 0 && <span>{selectedTest.total_question} questions</span>}
                    {selectedTest.total_marks > 0 && <span>{selectedTest.total_marks} marks</span>}
                    {selectedTest.difficulty_title && <span>{selectedTest.difficulty_title}</span>}
                  </div>
                </div>
              )}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-password">Create Password <span className="t16-req">*</span></label>
              <div className="t16-password-group">
                <input
                  id="t16-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={this.handleChange}
                  placeholder="Minimum 6 characters"
                  autoComplete="new-password"
                  className={`t16-input ${errors.password ? 't16-input-err' : ''}`}
                />
                <button
                  type="button"
                  className="t16-password-toggle"
                  onClick={() => this.setState({ showPassword: !showPassword })}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {errors.password
                ? <span className="t16-err">{errors.password}</span>
                : <span className="t16-hint">Use this password with your mobile number to log in to the exam app.</span>}
            </div>

            <div className="t16-field">
              <label htmlFor="t16-confirm-password">Confirm Password <span className="t16-req">*</span></label>
              <input
                id="t16-confirm-password"
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={this.handleChange}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                className={`t16-input ${errors.confirmPassword ? 't16-input-err' : ''}`}
              />
              {errors.confirmPassword && <span className="t16-err">{errors.confirmPassword}</span>}
            </div>

            <div className="t16-submit-row">
              <button type="submit" className="t16-submit" disabled={disabled || loader}>
                {loader
                  ? <><i className="fa fa-spinner fa-spin"></i> Submitting...</>
                  : <>Register for Free <i className="fa fa-arrow-right"></i></>}
              </button>
              {clientConfig.contactInfo.loginUrl && (
                <a href={clientConfig.contactInfo.loginUrl} className="t16-form-login" target="_blank" rel="noopener noreferrer">
                  <i className="fa fa-right-to-bracket"></i> Login
                </a>
              )}
            </div>

            <p className="t16-form-note">
              By registering you agree to be contacted by MD Group of Education regarding MDTSE 2026.
            </p>
          </form>
        </div>
      </div>
    );
  }

  render() {
    const { clientConfig, activeSyllabus, openFaq, showSuccessPopup, successDetail } = this.state;

    if (!clientConfig) {
      return (
        <div style={{ padding: '60px 20px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
          <h2 style={{ color: '#dc2626' }}>Configuration Error</h2>
          <p>Client configuration was not found for this form.</p>
        </div>
      );
    }

    const { branding, contactInfo } = clientConfig;
    const syllabus = SYLLABUS.find(s => s.key === activeSyllabus) || SYLLABUS[0];

    return (
      <div className="t16">
        {/* ------------------------------ Header ----------------------------- */}
        <header className="t16-header">
          <div className="t16-header-inner">
            <div className="t16-brand">
              {branding.logo
                ? <img src={branding.logo} alt={clientConfig.clientName} className="t16-logo" />
                : <span className="t16-logo-fallback">MD</span>}
              <div className="t16-brand-text">
                <h1 className="t16-brand-name">{clientConfig.clientName}</h1>
                <div className="t16-brand-sub">{branding.badgeText}</div>
              </div>
            </div>
            <div className="t16-header-actions">
              <a href={`tel:${contactInfo.phone1}`} className="t16-header-phone">
                <i className="fa fa-phone"></i> {contactInfo.phone1}
              </a>
              <button type="button" className="t16-header-cta" onClick={this.scrollToForm}>
                Register Free
              </button>
              {contactInfo.loginUrl && (
                <a href={contactInfo.loginUrl} className="t16-header-login" target="_blank" rel="noopener noreferrer">
                  <i className="fa fa-right-to-bracket"></i> Login
                </a>
              )}
            </div>
          </div>
        </header>

        {/* ------------------------------- Hero ------------------------------ */}
        <section className="t16-hero">
          <div className="t16-wrap t16-hero-grid">
            <div className="t16-hero-left">
              <span className="t16-eyebrow"><i className="fa fa-award"></i> 3rd Edition &middot; 2026</span>
              <h2 className="t16-hero-title">
                {branding.heroTitle}<br />
                <span className="t16-accent">Up to 100% Scholarship</span>
              </h2>
              <p className="t16-hero-sub">{branding.heroSubtitle}</p>

              <img src={mdtseBanner} alt="MDTSE 2026 - MD Talent Search Exam" className="t16-hero-banner" />

              <div className="t16-usp-row">
                <div className="t16-usp">
                  <div className="t16-usp-value">Up to 100%</div>
                  <div className="t16-usp-label">Scholarship</div>
                </div>
                <div className="t16-usp">
                  <div className="t16-usp-value">Up to 125 Lakh*</div>
                  <div className="t16-usp-label">Scholarships &amp; Cash Awards</div>
                </div>
                <div className="t16-usp">
                  <div className="t16-usp-value">Class VI - XII</div>
                  <div className="t16-usp-label">Studying Students</div>
                </div>
              </div>

            </div>

            {/* Own grid item so the form can jump ahead of it on mobile */}
            <div className="t16-hero-extra">
              <div className="t16-steps-card">
                <h3 className="t16-steps-title">How It Works</h3>
                <ol className="t16-steps">
                  {HOW_IT_WORKS.map((step, index) => (
                    <li className="t16-step" key={step.title}>
                      <span className="t16-step-marker">
                        <i className={`fa ${step.icon}`}></i>
                      </span>
                      <div className="t16-step-body">
                        <div className="t16-step-title">
                          <span className="t16-step-num">Step {index + 1}</span>
                          {step.title}
                        </div>
                        <p className="t16-step-desc">{step.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="t16-help-card">
                <div className="t16-help-icon"><i className="fa fa-headset"></i></div>
                <div className="t16-help-body">
                  <div className="t16-help-title">Need help registering?</div>
                  <p className="t16-help-desc">Our team can walk you through it. Call any of these numbers.</p>
                  <div className="t16-help-numbers">
                    {[contactInfo.phone1, contactInfo.phone2, contactInfo.phone3].filter(Boolean).map(phone => (
                      <a href={`tel:${phone}`} key={phone} className="t16-help-number">
                        <i className="fa fa-phone"></i> {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="t16-form-col">
              {this.renderForm()}
            </div>
          </div>
        </section>

        {/* ------------------------------- About ----------------------------- */}
        <section className="t16-section">
          <div className="t16-wrap">
            <div className="t16-section-head">
              <span className="t16-section-tag">About the Exam</span>
              <h2 className="t16-section-title">Welcome to MDTSE 2026</h2>
            </div>
            <p className="t16-about">
              <strong>MD Talent Search Exam (MDTSE)</strong> is a major scholarship test that gives students from
              Class 6 to 12 and 12th pass students an opportunity to earn up to 100% scholarship and cash awards*,
              making their path toward a career in Medical or Engineering easier. Its aim is to identify and nurture
              talent, and to provide the right guidance to students. Through the MDTSE scholarship, students can
              prepare for NEET, JEE (Main &amp; Advanced), State CET, Board Exams, NTSE, STSE, Olympiads and more
              under the expert mentorship of this prestigious institute.
            </p>
          </div>
        </section>

        {/* --------------------------- Exam schedule ------------------------- */}
        <section className="t16-section t16-section-alt">
          <div className="t16-wrap">
            <div className="t16-section-head">
              <span className="t16-section-tag">Exam Dates</span>
              <h2 className="t16-section-title">MDTSE 2026 Schedule</h2>
              <p className="t16-section-desc">Two online phases followed by an offline final round at MD Campus, Chirawa.</p>
            </div>

            <div className="t16-phase-grid">
              {EXAM_PHASES.map(phase => (
                <div className="t16-phase" key={phase.name}>
                  <div className="t16-phase-top">
                    <span className="t16-phase-name">{phase.name}</span>
                    <span className={`t16-mode-pill ${phase.mode === 'Online' ? 't16-mode-online' : 't16-mode-offline'}`}>
                      {phase.mode}
                    </span>
                  </div>
                  <div className="t16-phase-date">{phase.date}</div>
                  <div className="t16-phase-day">{phase.day}</div>
                  <div className="t16-phase-time"><i className="fa fa-clock"></i> {phase.time}</div>
                </div>
              ))}
            </div>

            <div className="t16-info-grid">
              {EXAM_INFO.map(info => (
                <div className="t16-info" key={info.label}>
                  <div className="t16-info-icon"><i className={`fa ${info.icon}`}></i></div>
                  <div>
                    <div className="t16-info-label">{info.label}</div>
                    <div className="t16-info-value">
                      {info.value}
                      <small>{info.sub}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------ Rewards ---------------------------- */}
        <section className="t16-section t16-section-navy">
          <div className="t16-wrap">
            <div className="t16-section-head">
              <span className="t16-section-tag">Rewards</span>
              <h2 className="t16-section-title">Scholarships, Cash Awards &amp; Recognition</h2>
              <p className="t16-section-desc">Top performers are rewarded across every participating class.</p>
            </div>

            <div className="t16-reward-grid">
              {REWARDS.map(reward => (
                <div className="t16-reward" key={reward.title}>
                  <div className="t16-reward-icon"><i className={`fa ${reward.icon}`}></i></div>
                  <div className="t16-reward-value">{reward.value}</div>
                  <div className="t16-reward-title">{reward.title}</div>
                  <p className="t16-reward-desc">{reward.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------ Features --------------------------- */}
        <section className="t16-section">
          <div className="t16-wrap">
            <div className="t16-section-head">
              <span className="t16-section-tag">Why MD Group</span>
              <h2 className="t16-section-title">What Every MDTSE Student Gets</h2>
            </div>

            <div className="t16-feature-grid">
              {FEATURES.map(feature => (
                <div className="t16-feature" key={feature.title}>
                  <div className="t16-feature-icon"><i className={`fa ${feature.icon}`}></i></div>
                  <div>
                    <div className="t16-feature-title">{feature.title}</div>
                    <p className="t16-feature-desc">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------ Pattern & syllabus ----------------------- */}
        <section className="t16-section t16-section-alt">
          <div className="t16-wrap">
            <div className="t16-section-head">
              <span className="t16-section-tag">Exam Pattern</span>
              <h2 className="t16-section-title">Pattern &amp; Syllabus</h2>
              <p className="t16-section-desc">Select your class to view the complete subject-wise syllabus.</p>
            </div>

            <div className="t16-pattern-strip">
              {PATTERN.map(item => (
                <div className="t16-pattern-card" key={item.label}>
                  <div className="t16-pattern-value">
                    {item.value}
                    <small>{item.sub}</small>
                  </div>
                  <div className="t16-pattern-label">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="t16-tabs">
              {SYLLABUS.map(item => (
                <button
                  type="button"
                  key={item.key}
                  className={`t16-tab ${activeSyllabus === item.key ? 'active' : ''}`}
                  onClick={() => this.setState({ activeSyllabus: item.key })}
                >
                  {item.tab}
                </button>
              ))}
            </div>

            <div className="t16-syllabus-card">
              <div className="t16-syllabus-head">
                <h3>{syllabus.title}</h3>
                <span className="t16-syllabus-marks">40 Questions &middot; 100 Marks &middot; 1 Hour</span>
              </div>
              <div className="t16-subject-grid">
                {syllabus.subjects.map(subject => (
                  <div className="t16-subject" key={subject.name}>
                    <div className="t16-subject-name">
                      <i className={`fa ${SUBJECT_ICONS[subject.name] || 'fa-book'}`}></i> {subject.name}
                    </div>
                    <ul className="t16-topic-list">
                      {subject.topics.map(topic => <li key={topic}>{topic}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="t16-syllabus-foot">{syllabus.marks}</div>
            </div>
          </div>
        </section>

        {/* -------------------------------- FAQ ------------------------------ */}
        <section className="t16-section">
          <div className="t16-wrap">
            <div className="t16-section-head">
              <span className="t16-section-tag">FAQ</span>
              <h2 className="t16-section-title">Frequently Asked Questions</h2>
            </div>

            <div className="t16-faq-list">
              {FAQS.map((faq, index) => (
                <div className={`t16-faq ${openFaq === index ? 'open' : ''}`} key={faq.q}>
                  <button
                    type="button"
                    className="t16-faq-q"
                    onClick={() => this.setState({ openFaq: openFaq === index ? -1 : index })}
                  >
                    {faq.q}
                    <i className="fa fa-chevron-down"></i>
                  </button>
                  <div className="t16-faq-a">{faq.a}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------ Footer ----------------------------- */}
        <footer className="t16-footer">
          <div className="t16-wrap t16-footer-grid">
            <div>
              <h4>{clientConfig.clientName}</h4>
              <div className="t16-footer-row">
                <i className="fa fa-location-dot"></i>
                <p>{contactInfo.address}</p>
              </div>
              {[contactInfo.website, contactInfo.website2].filter(Boolean).map(site => (
                <div className="t16-footer-row" key={site}>
                  <i className="fa fa-globe"></i>
                  <a href={`https://${site}`} target="_blank" rel="noopener noreferrer">
                    {site}
                  </a>
                </div>
              ))}
            </div>

            <div>
              <h4>Contact Us</h4>
              {[contactInfo.phone1, contactInfo.phone2, contactInfo.phone3].filter(Boolean).map(phone => (
                <div className="t16-footer-row" key={phone}>
                  <i className="fa fa-phone"></i>
                  <a href={`tel:${phone}`}>{phone}</a>
                </div>
              ))}
              <div className="t16-footer-row">
                <i className="fa fa-envelope"></i>
                <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
              </div>
            </div>

            <div>
              <h4>MDTSE 2026</h4>
              <div className="t16-footer-row"><i className="fa fa-calendar-days"></i><p>Phase 1 - 13 Sep 2026 (Online)</p></div>
              <div className="t16-footer-row"><i className="fa fa-calendar-days"></i><p>Phase 2 - 20 Sep 2026 (Online)</p></div>
              <div className="t16-footer-row"><i className="fa fa-calendar-days"></i><p>Final Round - 11 Oct 2026 (Offline)</p></div>
            </div>
          </div>

          <div className="t16-footer-bottom">
            <div>&copy; {new Date().getFullYear()} {clientConfig.clientName}. All rights reserved.</div>
            <div className="t16-disclaimer">*Terms &amp; conditions apply. Scholarships and cash awards are subject to exam performance and institute policy.</div>
          </div>
        </footer>

        {/* --------------------------- Mobile CTA ---------------------------- */}
        <div className="t16-mobile-cta">
          <a href={`tel:${contactInfo.phone1}`} className="t16-mobile-call">
            <i className="fa fa-phone"></i> Call Us
          </a>
          <button type="button" className="t16-mobile-register" onClick={this.scrollToForm}>
            Register Free
          </button>
        </div>

        {/* --------------------------- OTP modal ----------------------------- */}
        {this.state.otpRequired && this.renderOtpModal()}

        {/* -------------------------- Success popup -------------------------- */}
        {showSuccessPopup && (
          <div className="t16-overlay" onClick={() => this.setState({ showSuccessPopup: false })}>
            <div className="t16-popup" onClick={(e) => e.stopPropagation()}>
              <div className="t16-popup-icon"><i className="fa fa-circle-check"></i></div>
              <h3>Registration Successful!</h3>
              <p>Your MDTSE 2026 account is ready. Log in to the exam app with your mobile number and the password you just created.</p>

              {successDetail && (
                <div className="t16-popup-detail">
                  <div><span>Student</span><span>{successDetail.name}</span></div>
                  <div><span>Standard</span><span>{successDetail.standard}</span></div>
                  {successDetail.slot && <div><span>Exam Slot</span><span>{successDetail.slot}</span></div>}
                </div>
              )}

              {contactInfo.loginUrl && (
                <a href={contactInfo.loginUrl} className="t16-popup-btn" target="_blank" rel="noopener noreferrer">
                  <i className="fa fa-right-to-bracket"></i> Go to Login
                </a>
              )}
              <button type="button" className="t16-popup-btn t16-popup-btn-ghost" onClick={() => this.setState({ showSuccessPopup: false })}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(EnquiryTemplate16);
